import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { ProDriverReviewList } from "./ProDriverReviewList";
import { Card, CardContent } from "@/components/ui/card";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { CreateWorkOrderDriverReviewInput } from "@/gql/graphql";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { createProDriverReview } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderDriverMutations";
import { Rating } from "@/lib/Components/Rating/Rating";
import { Button } from "@/components/catalyst/button";
import { Field, useField, useFormikContext } from "formik";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";

const reviewSchema = z.object({
  comment: z.string().min(1, "Review is required"),
  rating: z.number().min(1, "Rating is required"),
  pro_driver_id: z.string(),
  work_order_assignment_id: z.string().optional(),
});

export function ProDriverReviewDialog({
  proDriverId,
  workOrderAssignmentId,
  initialRating = 0,
}: {
  proDriverId: string;
  workOrderAssignmentId?: string;
  initialRating?: number;
}) {
  return (
    <BaseDialog title="Reviews" className="max-w-4xl">
      <ProDriverReviewForm
        proDriverId={proDriverId}
        workOrderAssignmentId={workOrderAssignmentId}
        initialRating={initialRating}
      />
    </BaseDialog>
  );
}

export function ProDriverReviewForm({
  proDriverId,
  workOrderAssignmentId,
  initialRating = 0,
  onSubmitted,
}: {
  proDriverId: string;
  workOrderAssignmentId?: string;
  initialRating?: number;
  onSubmitted?: () => void;
}) {
  const { mutateAsync, isPending } = useGqlMutation(createProDriverReview);

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <ProDriverReviewList proDriverId={proDriverId} />
        </CardContent>
      </Card>

      <GenericForm<CreateWorkOrderDriverReviewInput>
        className="relative block"
        validationSchema={toFormikValidationSchema(reviewSchema)}
        initialValues={{
          rating: initialRating,
          comment: "",
          pro_driver_id: proDriverId,
          work_order_assignment_id: workOrderAssignmentId,
        }}
        onSubmit={async (values, formikHelpers) => {
          await mutateAsync({
            input: values,
          });

          formikHelpers.resetForm();
          onSubmitted?.();
        }}
      >
        <div className="overflow-hidden rounded-lg bg-white pb-12 shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-indigo-600">
          <Field
            as="textarea"
            rows={3}
            name="comment"
            autoFocus
            className="block w-full resize-none border-0 bg-transparent px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
            placeholder="Add a review..."
          />
        </div>

        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-3 py-2">
          <RatingField name="rating" />
          <div className="flex items-center gap-3">
            <FormErrors />
            <Button type="submit" color="indigo" disabled={isPending}>
              {isPending ? (
                <>
                  <Spinner className="size-4" />
                  Submitting...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      </GenericForm>
    </div>
  );
}

function RatingField({ name }: { name: string }) {
  const [{ value }, , { setValue }] = useField<number>(name);

  return (
    <Rating
      rating={value}
      maxRating={5}
      onRatingChange={(rating) => setValue(rating)}
      size="lg"
    />
  );
}

function FormErrors() {
  const { errors, touched, submitCount } =
    useFormikContext<CreateWorkOrderDriverReviewInput>();

  const hasBeenSubmitted = submitCount > 0;
  const errorMessages: string[] = [];

  if (hasBeenSubmitted || touched.rating) {
    if (errors.rating) errorMessages.push(errors.rating);
  }
  if (hasBeenSubmitted || touched.comment) {
    if (errors.comment) errorMessages.push(errors.comment);
  }

  if (errorMessages.length === 0) return null;

  return (
    <span className="text-xs text-red-600">{errorMessages.join(", ")}</span>
  );
}
